import { useEffect, useRef, useCallback, useState } from 'react';
import { FunctionEntry, PhysicsObject, GraphSettings } from '@/types/simulation';
import { useGraphCanvas } from '@/hooks/useGraphCanvas';
import { canvasToWorld } from '@/utils/physics';
import { ThemeMode } from '@/contexts/ThemeContext';

interface ImageOverlay {
  dataUrl: string;
  width: number;
  height: number;
  opacity?: number;
}

interface Props {
  functions: FunctionEntry[];
  objects: PhysicsObject[];
  settings: GraphSettings;
  showTrails: boolean;
  selectedObjectId: string | null;
  onObjectSelect: (id: string) => void;
  onObjectDrag: (id: string, wx: number, wy: number) => void;
  onSettingsChange?: (updates: Partial<GraphSettings>) => void;
  theme?: ThemeMode;
  imageOverlay?: ImageOverlay | null;
  onClearImageOverlay?: () => void;
}

export default function GraphCanvas({
  functions,
  objects,
  settings,
  showTrails,
  selectedObjectId,
  onObjectSelect,
  onObjectDrag,
  onSettingsChange,
  theme = 'dark',
  imageOverlay,
  onClearImageOverlay,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef({ width: 800, height: 600 });
  const draggingRef = useRef<string | null>(null);
  const didDragRef = useRef(false);
  // Pan state
  const panningRef = useRef(false);
  const panStartRef = useRef<{ cx: number; cy: number; xMin: number; xMax: number; yMin: number; yMax: number } | null>(null);
  // Store latest settings in a ref for use in wheel/pan handlers without stale closure
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  // Tracking
  const [tracking, setTracking] = useState(false);
  const trackingRef = useRef(false);
  const onSettingsChangeRef = useRef(onSettingsChange);
  const objectsRef = useRef(objects);

  useEffect(() => { trackingRef.current = tracking; }, [tracking]);
  useEffect(() => { onSettingsChangeRef.current = onSettingsChange; }, [onSettingsChange]);
  useEffect(() => { objectsRef.current = objects; }, [objects]);

  const { canvasRef, draw } = useGraphCanvas(
    sizeRef.current.width,
    sizeRef.current.height
  );

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        sizeRef.current = {
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        };
        if (canvasRef.current) {
          canvasRef.current.width = sizeRef.current.width;
          canvasRef.current.height = sizeRef.current.height;
        }
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [canvasRef]);

  useEffect(() => {
    draw({ functions, objects, settings, showTrails, selectedObjectId, theme });
  });

  // ---- Tracking loop ----
  useEffect(() => {
    let raf: number;
    const loop = () => {
      if (trackingRef.current && selectedObjectId && onSettingsChangeRef.current) {
        const target = objectsRef.current.find((o) => o.id === selectedObjectId);
        if (target) {
          const s = settingsRef.current;
          const xRange = s.xMax - s.xMin;
          const yRange = s.yMax - s.yMin;
          const targetXMin = target.x - xRange / 2;
          const targetXMax = target.x + xRange / 2;
          const targetYMin = target.y - yRange / 2;
          const targetYMax = target.y + yRange / 2;
          const lerp = 0.12;
          onSettingsChangeRef.current({
            xMin: s.xMin + (targetXMin - s.xMin) * lerp,
            xMax: s.xMax + (targetXMax - s.xMax) * lerp,
            yMin: s.yMin + (targetYMin - s.yMin) * lerp,
            yMax: s.yMax + (targetYMax - s.yMax) * lerp,
          });
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [selectedObjectId]);

  // ---- Wheel zoom (centered on cursor) ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = settingsRef.current;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const wx = s.xMin + (mx / canvas.width) * (s.xMax - s.xMin);
      const wy = s.yMin + ((canvas.height - my) / canvas.height) * (s.yMax - s.yMin);
      const zoomFactor = e.deltaY < 0 ? 0.92 : 1 / 0.92;
      const newXMin = wx - (wx - s.xMin) * zoomFactor;
      const newXMax = wx + (s.xMax - wx) * zoomFactor;
      const newYMin = wy - (wy - s.yMin) * zoomFactor;
      const newYMax = wy + (s.yMax - wy) * zoomFactor;
      const xRange = newXMax - newXMin;
      const yRange = newYMax - newYMin;
      if (xRange < 0.01 || xRange > 10000 || yRange < 0.01 || yRange > 10000) return;
      onSettingsChange?.({ xMin: newXMin, xMax: newXMax, yMin: newYMin, yMax: newYMax });
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [canvasRef, onSettingsChange]);

  const getWorldPos = useCallback(
    (e: React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      return canvasToWorld(cx, cy, {
        settings,
        width: canvas.width,
        height: canvas.height,
      });
    },
    [canvasRef, settings]
  );

  const getObjectAtPos = useCallback(
    (wx: number, wy: number): string | null => {
      if (!canvasRef.current) return null;
      // size はワール単位の直径 → 物理計算と完全一致
      for (const obj of [...objects].reverse()) {
        const dx = wx - obj.x;
        const dy = wy - obj.y;
        const radiusWorld = obj.size / 2;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= radiusWorld * 1.2) return obj.id;
      }
      return null;
    },
    [objects, canvasRef]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 1 || e.button === 2) {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const s = settingsRef.current;
        panningRef.current = true;
        panStartRef.current = {
          cx: e.clientX - rect.left,
          cy: e.clientY - rect.top,
          xMin: s.xMin, xMax: s.xMax,
          yMin: s.yMin, yMax: s.yMax,
        };
        if (trackingRef.current) setTracking(false);
        return;
      }
      const pos = getWorldPos(e);
      if (!pos) return;
      const hit = getObjectAtPos(pos.wx, pos.wy);
      if (hit) {
        draggingRef.current = hit;
        didDragRef.current = false;
      } else {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const s = settingsRef.current;
        panningRef.current = true;
        panStartRef.current = {
          cx: e.clientX - rect.left,
          cy: e.clientY - rect.top,
          xMin: s.xMin, xMax: s.xMax,
          yMin: s.yMin, yMax: s.yMax,
        };
        if (trackingRef.current) setTracking(false);
      }
    },
    [getWorldPos, getObjectAtPos, canvasRef]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (draggingRef.current) {
        didDragRef.current = true;
        const pos = getWorldPos(e);
        if (!pos) return;
        onObjectDrag(draggingRef.current, pos.wx, pos.wy);
        return;
      }
      if (panningRef.current && panStartRef.current) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const p = panStartRef.current;
        const dx = (e.clientX - rect.left) - p.cx;
        const dy = (e.clientY - rect.top) - p.cy;
        const xRange = p.xMax - p.xMin;
        const yRange = p.yMax - p.yMin;
        const worldDx = -(dx / canvas.width) * xRange;
        const worldDy = (dy / canvas.height) * yRange;
        onSettingsChange?.({
          xMin: p.xMin + worldDx,
          xMax: p.xMax + worldDx,
          yMin: p.yMin + worldDy,
          yMax: p.yMax + worldDy,
        });
      }
    },
    [getWorldPos, onObjectDrag, onSettingsChange, canvasRef]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (panningRef.current) {
        panningRef.current = false;
        panStartRef.current = null;
        return;
      }
      if (draggingRef.current && !didDragRef.current) {
        const pos = getWorldPos(e);
        if (pos) {
          const hit = getObjectAtPos(pos.wx, pos.wy);
          if (hit) onObjectSelect(hit);
        }
      }
      draggingRef.current = null;
      didDragRef.current = false;
    },
    [getWorldPos, getObjectAtPos, onObjectSelect]
  );

  const handleResetView = () => {
    const s = settingsRef.current;
    const xHalf = (s.xMax - s.xMin) / 2;
    const yHalf = (s.yMax - s.yMin) / 2;
    onSettingsChange?.({ xMin: -xHalf, xMax: xHalf, yMin: -yHalf, yMax: yHalf });
    setTracking(false);
  };

  const handleToggleTracking = () => {
    const next = !tracking;
    setTracking(next);
    if (next && selectedObjectId) {
      const target = objects.find((o) => o.id === selectedObjectId);
      if (target) {
        const s = settingsRef.current;
        const xRange = s.xMax - s.xMin;
        const yRange = s.yMax - s.yMin;
        onSettingsChange?.({
          xMin: target.x - xRange / 2,
          xMax: target.x + xRange / 2,
          yMin: target.y - yRange / 2,
          yMax: target.y + yRange / 2,
        });
      }
    }
  };

  const isDark = theme === 'dark';
  const hintTextClass = isDark ? 'text-white/20' : 'text-gray-400';
  const badgeTextClass = isDark ? 'text-white/35' : 'text-gray-500';
  const btnBase = `glass-pill ripple-btn w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`;
  const trackingActive = tracking && !!selectedObjectId;

  const getCursor = () => {
    if (draggingRef.current) return 'grabbing';
    if (panningRef.current) return 'grabbing';
    return 'grab';
  };

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {/* Image overlay */}
      {imageOverlay && (
        <div className="absolute inset-0 pointer-events-none z-10">
          <img
            src={imageOverlay.dataUrl}
            alt="overlay"
            className="w-full h-full object-contain"
            style={{ opacity: imageOverlay.opacity ?? 0.35 }}
          />
          {onClearImageOverlay && (
            <button
              onClick={onClearImageOverlay}
              className="pointer-events-auto absolute top-12 right-3 glass-pill ripple-btn px-2.5 py-1 text-xs rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-orange-400 ring-1 ring-orange-400/30 z-20"
              title="画像オーバーレイを削除"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-image-2-line text-xs" />
              </div>
              画像を削除
            </button>
          )}
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ cursor: getCursor() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          draggingRef.current = null;
          didDragRef.current = false;
          panningRef.current = false;
          panStartRef.current = null;
        }}
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* Zoom controls + tracking */}
      <div className="no-print absolute bottom-3 left-3 flex items-center gap-1.5">
        <button
          onClick={() => {
            const s = settingsRef.current;
            const cx = (s.xMin + s.xMax) / 2;
            const cy = (s.yMin + s.yMax) / 2;
            const xRange = (s.xMax - s.xMin) * 0.85;
            const yRange = (s.yMax - s.yMin) * 0.85;
            onSettingsChange?.({
              xMin: cx - xRange / 2, xMax: cx + xRange / 2,
              yMin: cy - yRange / 2, yMax: cy + yRange / 2,
            });
          }}
          className={btnBase}
          title="ズームイン"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-zoom-in-line text-sm" /></div>
        </button>
        <button
          onClick={() => {
            const s = settingsRef.current;
            const cx = (s.xMin + s.xMax) / 2;
            const cy = (s.yMin + s.yMax) / 2;
            const xRange = (s.xMax - s.xMin) / 0.85;
            const yRange = (s.yMax - s.yMin) / 0.85;
            onSettingsChange?.({
              xMin: cx - xRange / 2, xMax: cx + xRange / 2,
              yMin: cy - yRange / 2, yMax: cy + yRange / 2,
            });
          }}
          className={btnBase}
          title="ズームアウト"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-zoom-out-line text-sm" /></div>
        </button>
        <button onClick={handleResetView} className={btnBase} title="視点を原点に戻す">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-focus-3-line text-sm" /></div>
        </button>
        <button
          onClick={() => onSettingsChange?.({ xMin: -10, xMax: 10, yMin: -6, yMax: 6 })}
          className={btnBase}
          title="表示をデフォルトに戻す"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-fullscreen-line text-sm" /></div>
        </button>

        {/* Tracking button */}
        <button
          onClick={handleToggleTracking}
          className={`glass-pill ripple-btn w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 ${
            trackingActive
              ? 'text-emerald-400 ring-1 ring-emerald-400/50'
              : isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
          }`}
          title={trackingActive ? 'トラッキング中（クリックで解除）' : '選択物体をトラッキング'}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-crosshair-2-line text-sm ${trackingActive ? 'animate-pulse' : ''}`} />
          </div>
        </button>
      </div>

      <div className={`no-print absolute bottom-3 right-3 glass-pill px-3 py-1.5 text-xs ${hintTextClass} pointer-events-none select-none rounded-full`}>
        スクロール:ズーム　ドラッグ:移動
      </div>

      <div className={`no-print absolute top-3 left-3 glass-pill px-3 py-1.5 text-xs ${badgeTextClass} pointer-events-none select-none rounded-full flex items-center gap-1.5`}>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-crop-line ${isDark ? 'text-cyan-400' : 'text-sky-600'}`} />
        </div>
        2D
      </div>

      {/* Tracking indicator */}
      {trackingActive && (
        <div className="no-print absolute top-3 right-3 glass-pill px-3 py-1.5 text-xs select-none rounded-full flex items-center gap-1.5 text-emerald-400 ring-1 ring-emerald-400/30">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-crosshair-2-line text-xs animate-pulse" />
          </div>
          トラッキング中
        </div>
      )}
    </div>
  );
}
